import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { api, newIdempotencyKey } from '../api';
import { DecoRule, OakMark, Wordmark } from '../components/brand';
import { ErrorBox, QueryView } from '../components/ui';
import { formatMoney, whenAt } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

/**
 * Administration → Payments (owner, decision 94): whether Square is set up, the webhook address to give Square, the
 * switch for the Pay button on the emailed invoice, a $1 test link, and the last payments Square reported.
 */
export function PaymentsSettingsPage() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const status = useQuery({ queryKey: qk.squareStatus, queryFn: ({ signal }) => api.squareStatus(signal) });
  const save = useMutation({ mutationFn: (live: boolean) => api.saveSquareSettings(live), onSuccess: (s) => qc.setQueryData(qk.squareStatus, s) });
  const testKey = useRef(newIdempotencyKey());
  const test = useMutation({
    mutationFn: () => api.squareTestLink(testKey.current),
    onSuccess: (link) => {
      testKey.current = newIdempotencyKey();
      window.open(link.url, '_blank', 'noopener');
    },
  });
  const money = (v: number) => formatMoney(v, intlLocale);

  return (
    <div className="desk-page" data-testid="payments-settings">
      <header className="desk-head">
        <div>
          <h1>{t('paymentsTitle')}</h1>
          <p className="muted">{t('paymentsHint')}</p>
        </div>
      </header>
      <QueryView query={status}>
        {(s) => (
          <div className="stack">
            <section className="panel stack-sm">
              <h2>{t('squareServer')}</h2>
              {s.configured ? (
                <p className="ok-band" role="status">
                  {s.sandbox ? t('squareSandbox') : t('squareConfigured')}
                </p>
              ) : (
                <>
                  <p className="warn-band">{t('squareNotSetUp')}</p>
                  <p className="muted">{t('squareSetupHint')}</p>
                </>
              )}
              <label className="field">
                <span>{t('squareWebhookUrl')}</span>
                <input type="text" readOnly value={s.webhookUrl} onFocus={(e) => e.target.select()} />
              </label>
              <p className="muted">{t('squareWebhookHint')}</p>
            </section>
            <section className="panel stack-sm">
              <h2>{t('squarePayButton')}</h2>
              <label className="check">
                <input type="checkbox" checked={s.live} disabled={save.isPending} onChange={(e) => save.mutate(e.target.checked)} /> {t('squareLiveLabel')}
              </label>
              <p className="muted">{t('squareLiveHint')}</p>
              {save.error ? <ErrorBox error={save.error} /> : null}
            </section>
            <section className="panel stack-sm">
              <h2>{t('squareTestTitle')}</h2>
              <p className="muted">{t('squareTestHint')}</p>
              <div className="row-actions">
                <button type="button" className="btn btn-secondary" disabled={!s.configured || test.isPending} onClick={() => test.mutate()}>
                  {t('squareTestButton')}
                </button>
              </div>
              {test.error ? <ErrorBox error={test.error} /> : null}
            </section>
            <section className="panel stack-sm">
              <h2>{t('squareRecent')}</h2>
              {s.recent.length ? (
                <div className="table-wrap">
                  <table className="data-table" data-testid="square-recent">
                    <thead>
                      <tr>
                        <th>{t('squarePaidAt')}</th>
                        <th>{t('squareCustomer')}</th>
                        <th>{t('invoice')}</th>
                        <th className="al-right">{t('amountCol')}</th>
                        <th className="al-right">{t('squareFee')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.recent.map((r) => (
                        <tr key={r.id}>
                          <td>{whenAt(r.paidAt, intlLocale)}</td>
                          <td>{r.test ? <span className="badge">{t('emailTestBadge')}</span> : (r.customerName ?? '—')}</td>
                          <td>{r.invoiceNo ?? '—'}</td>
                          <td className="al-right">{money(r.amount)}</td>
                          <td className="al-right">{r.fee === null ? '—' : money(r.fee)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="muted">{t('squareNoPayments')}</p>
              )}
            </section>
          </div>
        )}
      </QueryView>
    </div>
  );
}

/** Where Square sends the customer after paying: no sign-in, nothing about the invoice (the address carries no key). */
export function PaidPage() {
  const { t } = useLocale();
  return (
    <main className="public-doc stack" data-testid="paid-page">
      <header className="letterhead">
        <OakMark />
        <Wordmark />
        <DecoRule />
      </header>
      <section className="card stack-sm">
        <h1>{t('paidThanksTitle')}</h1>
        <p>{t('paidThanksBody')}</p>
      </section>
    </main>
  );
}
